import React, { useCallback, useEffect, useRef, useState } from "react";
import { Combobox, Portal } from "@headlessui/react";
import clsx from "clsx";

export interface AutocompleteItem {
  id: string;
  name: string;
  logoUrl: string;
  trailingItemElem?: React.ReactNode;
}

const DROPDOWN_CLASS = "max-h-[264px] scroll-py-3 overflow-y-auto p-3 bg-white rounded-md";
const DROPDOWN_STYLE = {
  scrollbarWidth: "none" as const,
  boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.25)",
};

export default function Autocomplete({
  items,
  selectedItem,
  onSelect,
  leadingElement,
  leadingInputPaddingPx,
  trailingElement,
  initialSuggestionsCount = 0,
  dropdownWidth = "w-[650px]",
  portalDropdown = false,
}: {
  items: AutocompleteItem[];
  selectedItem: AutocompleteItem | null | undefined;
  onSelect: (item: AutocompleteItem) => Promise<void>;
  leadingElement?: React.ReactNode;
  leadingInputPaddingPx?: number;
  trailingElement?: React.ReactNode;
  initialSuggestionsCount?: number;
  dropdownWidth?: string;
  /** Render the dropdown outside overflow-clipping ancestors, such as modal content panes. */
  portalDropdown?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  // Do not rely solely on Headless UI opening during the render that replaces
  // the custom initial suggestions with Combobox.Options.
  const [showFilteredSuggestions, setShowFilteredSuggestions] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const portalRootRef = useRef<HTMLElement | null>(null);
  const [portalPosition, setPortalPosition] = useState({ left: 0, top: 0 });

  const setInputContainerRef = useCallback((element: HTMLDivElement | null) => {
    inputContainerRef.current = element;
    portalRootRef.current = element?.closest<HTMLElement>(".tw-boundary") ?? null;
  }, []);

  const updatePortalPosition = useCallback(() => {
    const rect = inputContainerRef.current?.getBoundingClientRect();
    if (rect) {
      setPortalPosition({ left: rect.left, top: rect.bottom });
    }
  }, []);

  useEffect(() => {
    if (!portalDropdown) return;

    updatePortalPosition();
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);
    return () => {
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [portalDropdown, updatePortalPosition]);

  const filteredItems =
    query === ""
      ? []
      : items
          .filter((item) => {
            return item.name.toLowerCase().includes(query.toLowerCase());
          })
          .slice(0, 10);

  // Initial suggestions use a plain div instead of Combobox.Options because
  // HeadlessUI's Combobox auto-activates the first option when opened and
  // commits that selection on blur, which would cause unwanted selections
  // when the user clicks away from an empty input.
  const showInitialSuggestions = initialSuggestionsCount > 0 && isFocused && query === "";
  const dropdownClassName = clsx(
    DROPDOWN_CLASS,
    portalDropdown ? "fixed z-[15002]" : "absolute",
    dropdownWidth,
  );
  const dropdownStyle = portalDropdown
    ? { ...DROPDOWN_STYLE, left: portalPosition.left, top: portalPosition.top }
    : DROPDOWN_STYLE;
  const renderDropdown = (dropdown: React.ReactElement) => {
    if (!portalDropdown) return dropdown;

    return (
      <Portal.Group target={portalRootRef}>
        <Portal>{dropdown}</Portal>
      </Portal.Group>
    );
  };

  return (
    <Combobox
      onChange={
        initialSuggestionsCount > 0
          ? async (item: AutocompleteItem) => {
              setShowFilteredSuggestions(false);
              await onSelect(item);
            }
          : onSelect
      }
      value={selectedItem}
    >
      <div ref={setInputContainerRef}>
        <div className="grid grid-cols-1 grow">
          <Combobox.Input
            autoFocus={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            className="col-start-1 row-start-1 w-full border-t border-r border-b rounded-r-sm !border-neutral-400 focus:!border-neutral-400 focus:!outline-none focus-visible:!outline-none focus:!shadow-none focus-visible:!shadow-none"
            style={{
              paddingLeft: leadingInputPaddingPx,
            }}
            placeholder="Start typing to search vendors by name"
            displayValue={(item: AutocompleteItem) => (item ? item.name : "")}
            onChange={(event) => {
              setQuery(event.target.value);
              if (initialSuggestionsCount > 0) {
                setShowFilteredSuggestions(event.target.value !== "");
              }
            }}
            onFocus={
              portalDropdown || initialSuggestionsCount > 0
                ? () => {
                    if (portalDropdown) updatePortalPosition();
                    if (initialSuggestionsCount > 0) setIsFocused(true);
                  }
                : undefined
            }
            onBlur={
              initialSuggestionsCount > 0
                ? () => {
                    setIsFocused(false);
                    setShowFilteredSuggestions(false);
                  }
                : undefined
            }
            onKeyDown={
              initialSuggestionsCount > 0
                ? (e: React.KeyboardEvent) => {
                    if (e.key === "Escape") {
                      setIsFocused(false);
                      setShowFilteredSuggestions(false);
                    }
                  }
                : undefined
            }
          />
          {/* pointer-events-none is necessary so clicks pass through to the input */}
          <div className="col-start-1 row-start-1 pointer-events-none">{leadingElement}</div>
          <div className="col-start-1 row-start-1 justify-self-end">{trailingElement}</div>
        </div>
      </div>

      {/* preventDefault on mousedown keeps focus on the input so onBlur
          doesn't fire before the item's onClick registers */}
      {showInitialSuggestions &&
        renderDropdown(
          <div
            onMouseDown={(e) => e.preventDefault()}
            className={dropdownClassName}
            style={dropdownStyle}
          >
            {items.slice(0, initialSuggestionsCount).map((item) => (
              <div
                key={item.id}
                className="group flex items-center gap-2 mb-1 px-4 py-5 border border-solid border-neutral-200 hover:border-neutral-900 rounded cursor-pointer"
                onClick={() => {
                  setIsFocused(false);
                  onSelect(item);
                }}
              >
                <img src={item.logoUrl} alt={item.name} className="h-6 w-6" />
                <div className="group-hover:font-medium">{item.name}</div>
                {item.trailingItemElem && <div className="ml-[12px]">{item.trailingItemElem}</div>}
              </div>
            ))}
          </div>,
        )}

      {/* Unmount Combobox.Options while initial suggestions are visible to
          avoid rendering an empty HeadlessUI dropdown behind them */}
      {!showInitialSuggestions &&
        renderDropdown(
          <Combobox.Options
            static={initialSuggestionsCount > 0 ? showFilteredSuggestions : undefined}
            className={dropdownClassName}
            style={dropdownStyle}
          >
            {filteredItems.map((item) => (
              <Combobox.Option key={item.id} value={item} as={"div"}>
                {({ active }) => (
                  <div
                    className={`flex items-center gap-2 mb-1 px-4 py-5 border border-solid ${
                      active ? "border-neutral-900" : "border-neutral-200"
                    } rounded`}
                  >
                    <img src={item.logoUrl} alt={item.name} className="h-6 w-6" />
                    <div className={`${active ? "font-medium" : ""}`}>{item.name}</div>
                    {item.trailingItemElem && (
                      <div className="ml-[12px]">{item.trailingItemElem}</div>
                    )}
                  </div>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>,
        )}
    </Combobox>
  );
}
